import React, { useEffect, useState } from 'react';
import {
  fetchAllCities,
  describeCode,
  CITIES,
  CITY_KEYS,
  CityKey,
  CityWeather,
} from '../utils/KarakotoWeather';

const CYCLE_INTERVAL_MS = 6000;

const KarakotoWeatherBadge: React.FC = () => {
  const [data, setData] = useState<Record<CityKey, CityWeather | null>>({
    karakoto: null, lyon: null, beirut: null, kampala: null,
  });
  const [idx, setIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAllCities().then((all) => { if (!cancelled) setData(all); });
    const refresh = setInterval(() => {
      fetchAllCities().then((all) => { if (!cancelled) setData(all); });
    }, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(refresh); };
  }, []);

  // Auto-cycle through cities (skip cities with no data yet)
  useEffect(() => {
    if (expanded) return; // pause cycle while expanded
    const tick = setInterval(() => {
      setIdx((i) => {
        // Find next index with valid data
        for (let off = 1; off <= CITY_KEYS.length; off++) {
          const next = (i + off) % CITY_KEYS.length;
          if (data[CITY_KEYS[next]]) return next;
        }
        return i;
      });
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(tick);
  }, [data, expanded]);

  // Manual click: advance to next city
  const advance = () => {
    setIdx((i) => {
      for (let off = 1; off <= CITY_KEYS.length; off++) {
        const next = (i + off) % CITY_KEYS.length;
        if (data[CITY_KEYS[next]]) return next;
      }
      return i;
    });
  };

  const currentKey = CITY_KEYS[idx];
  const w = data[currentKey];
  const city = CITIES[currentKey];

  if (!w) return null;

  const code = describeCode(w.weatherCode);

  return (
    <div
      className="fixed bottom-3 left-3 z-30 text-[#5fe979] font-mono select-none transition-opacity"
      style={{ opacity: 0.6, fontSize: '0.75rem' }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
    >
      <div
        className="flex items-center gap-1 cursor-pointer"
        onClick={advance}
        onAuxClick={() => setExpanded((v) => !v)}
        title="Click: next city — right-click: toggle details"
      >
        <span style={{ fontSize: '1rem' }}>{code.emoji}</span>
        <span>{city.flag}</span>
        <span>
          {city.labelLocal ?? city.label}: {Math.round(w.temperatureC)}°C {code.label}
        </span>
        <button
          className="ml-2 opacity-50 hover:opacity-100"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          aria-label="Toggle details"
        >
          {expanded ? '▾' : '▸'}
        </button>
      </div>
      {expanded && (
        <div className="mt-1 ml-1 opacity-80 border-l-2 border-[var(--terminal-border)] pl-2">
          {CITY_KEYS.map((k) => {
            const cw = data[k];
            const cd = CITIES[k];
            if (!cw) return (
              <div key={k} className="opacity-40">{cd.flag} {cd.label}: …</div>
            );
            const cc = describeCode(cw.weatherCode);
            return (
              <div key={k}>
                {cd.flag} {cd.label}: {cc.emoji} {Math.round(cw.temperatureC)}°C — {cc.label}
                <span className="opacity-50"> · wind {Math.round(cw.windKph)} km/h</span>
              </div>
            );
          })}
          <div className="opacity-50 mt-1">via Open-Meteo · click badge: next city</div>
        </div>
      )}
    </div>
  );
};

export default KarakotoWeatherBadge;
